import React from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Box, Button, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import * as Constants from '../../utilities/Constants';

/**
 * What a tenant owes, shown above their units.
 *
 * Deliberately not a separate page they have to go looking for. A tenant who has fallen behind
 * usually does not know it - the card failed silently weeks ago - so the balance has to be on the
 * screen they already visit, with the way to clear it next to the figure.
 *
 * Three states, because they call for different words:
 *  - overdue, which is money carried over from a month that has already closed
 *  - due, which is this month's rent that has not been collected yet and may still go through
 *  - clear, which is a single quiet line rather than a panel, so the good case stays out of the way
 */
export default function AccountBalancePanel(props) {
    const { statement, loading, onPay, isMobile } = props;

    if (loading) {
        return <Skeleton variant={'rounded'} height={96} sx={{ marginBottom: '16px' }} />;
    }

    if (!statement) {
        return null;
    }

    // Billed outside the application, so the ledger holds nothing for them and the balance below
    // would read zero for the wrong reason. They get sent to a person instead of a figure.
    if (!statement.balanceIsTracked) {
        return (
            <Alert severity={'info'} icon={false} sx={{ marginBottom: '16px' }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Your account is billed directly</AlertTitle>
                <Typography variant={'body2'}>
                    Your rent is arranged with the facility rather than charged to a card here, so we
                    cannot show your balance on this page. Please contact us for anything to do with
                    your account or payments.
                </Typography>
                <Button component={Link} to={'/contact'} variant={'outlined'} sx={{ marginTop: '12px' }}>
                    Contact us
                </Button>
            </Alert>
        );
    }

    if (!statement.hasBalance) {
        return (
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{ marginBottom: '16px' }}
            >
                <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                    {statement.balance < 0
                        ? `Your account is ${Constants.USDollar.format(Math.abs(statement.balance))} in credit.`
                        : 'Your account is up to date.'}
                </Typography>
                <Button component={Link} to={'/account-statement'} size={'small'}>
                    View statement
                </Button>
            </Stack>
        );
    }

    const overdue = statement.isPastDue;

    return (
        <Alert
            severity={overdue ? 'error' : 'info'}
            icon={false}
            sx={{ marginBottom: '16px', alignItems: 'stretch' }}
        >
            <AlertTitle sx={{ fontWeight: 700 }}>
                {overdue ? 'Your account is past due' : 'You have a balance due'}
            </AlertTitle>

            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                alignItems={isMobile ? 'stretch' : 'center'}
                justifyContent={'space-between'}
            >
                <Box>
                    <Typography variant={'h5'} component={'p'} sx={{ fontWeight: 700 }}>
                        {Constants.USDollar.format(statement.balance)}
                    </Typography>

                    {/* Only split the figure when there is something in both halves. Telling a
                        tenant who owes one month that $0.00 of it is overdue is noise. */}
                    {overdue && statement.currentDue > 0 &&
                        <Stack
                            direction={'row'}
                            spacing={1}
                            divider={<Divider orientation={'vertical'} flexItem />}
                            sx={{ marginTop: '4px' }}
                        >
                            <Typography variant={'body2'}>
                                {Constants.USDollar.format(statement.pastDue)} overdue
                            </Typography>
                            <Typography variant={'body2'}>
                                {Constants.USDollar.format(statement.currentDue)} due this month
                            </Typography>
                        </Stack>
                    }

                    <Typography variant={'body2'} sx={{ marginTop: '4px' }}>
                        {statement.unitNames.length > 0
                            ? `For ${statement.unitNames.join(', ')}.`
                            : ''}
                        {statement.canPayOnline
                            ? ' Paying below brings your account fully up to date.'
                            : ' Please contact us to arrange payment.'}
                    </Typography>
                </Box>

                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={1}
                    sx={{ flexShrink: 0 }}
                >
                    <Button component={Link} to={'/account-statement'} variant={'outlined'}>
                        View statement
                    </Button>
                    {statement.canPayOnline &&
                        <Button
                            variant={'contained'}
                            color={overdue ? 'error' : 'primary'}
                            onClick={onPay}
                        >
                            {`Pay ${Constants.USDollar.format(statement.balance)}`}
                        </Button>
                    }
                </Stack>
            </Stack>
        </Alert>
    );
}

AccountBalancePanel.propTypes = {
    /** The tenant's statement, or null before it has loaded. */
    statement: PropTypes.shape({
        balance: PropTypes.number,
        pastDue: PropTypes.number,
        currentDue: PropTypes.number,
        hasBalance: PropTypes.bool,
        isPastDue: PropTypes.bool,

        /** False when a unit is billed outside the app, so no figure may be shown. */
        balanceIsTracked: PropTypes.bool,

        canPayOnline: PropTypes.bool,
        unitNames: PropTypes.arrayOf(PropTypes.string),
    }),
    loading: PropTypes.bool,
    onPay: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
};
